<template>
  <div class="relative rounded-lg border border-slate-200/60 bg-slate-50/50 px-3 py-2.5 dark:border-slate-700/60 dark:bg-slate-800/30">
    <!-- Header - compact inline -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <CheckCircleIcon class="h-4 w-4 text-green-500 dark:text-green-400" />
        <span class="text-sm font-medium text-slate-600 dark:text-slate-300">Applied:</span>
        <span v-if="addedCount > 0" class="text-sm font-semibold text-green-600 dark:text-green-400">
          +{{ addedCount }}
        </span>
        <span v-if="modifiedCount > 0" class="text-sm font-semibold text-amber-600 dark:text-amber-400">
          ~{{ modifiedCount }}
        </span>
        <span v-if="removedCount > 0" class="text-sm font-semibold text-red-600 dark:text-red-400">
          -{{ removedCount }}
        </span>
      </div>

      <!-- Undone indicator -->
      <span
        v-if="isUndone"
        class="flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-400 dark:text-slate-500"
      >
        <CheckIcon class="h-3 w-3" />
        Undone
      </span>

      <!-- Undo button (only on most recent changelog, not yet undone) -->
      <button
        v-else-if="isLatest"
        @click="handleUndo"
        :disabled="isUndoing"
        class="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors"
        :class="isUndoing
          ? 'cursor-not-allowed text-slate-400 dark:text-slate-500'
          : 'text-slate-500 hover:bg-slate-200/60 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-700/60 dark:hover:text-slate-200'"
      >
        <template v-if="isUndoing">
          <svg class="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Undoing...
        </template>
        <template v-else>
          <ArrowUturnLeftIcon class="h-3 w-3" />
          Undo
        </template>
      </button>
    </div>

    <!-- Changes list (collapsible) -->
    <div v-if="changelog.length" class="mt-2">
      <button
        @click="isExpanded = !isExpanded"
        class="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
      >
        <ChevronRightIcon
          class="h-3 w-3 transition-transform"
          :class="{ 'rotate-90': isExpanded }"
        />
        Show changes
      </button>

      <Transition
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="max-h-0 opacity-0"
        enter-to-class="max-h-[500px] opacity-100"
        leave-active-class="transition-all duration-150 ease-in"
        leave-from-class="max-h-[500px] opacity-100"
        leave-to-class="max-h-0 opacity-0"
      >
        <div v-if="isExpanded" class="mt-2 space-y-0.5 overflow-hidden">
          <div
            v-for="(item, index) in changelog"
            :key="index"
            class="flex items-center gap-1.5 py-0.5 text-xs"
          >
            <!-- Type indicator -->
            <span
              class="flex-shrink-0 font-mono font-medium"
              :class="{
                'text-green-600 dark:text-green-400': item.type === 'add',
                'text-amber-600 dark:text-amber-400': item.type === 'modify',
                'text-red-600 dark:text-red-400': item.type === 'remove'
              }"
            >
              {{ item.type === 'add' ? '+' : item.type === 'remove' ? '-' : '~' }}
            </span>

            <!-- Resource label -->
            <span class="font-medium text-slate-600 dark:text-slate-300">
              {{ getResourceLabel(item.resourceType) }}
            </span>

            <!-- Resource name (if available) -->
            <span v-if="item.name" class="truncate text-slate-500 dark:text-slate-400">
              {{ item.name }}
            </span>

            <!-- Position info -->
            <span v-if="item.afterNodeId" class="text-slate-400 dark:text-slate-500 font-mono truncate">
              after {{ truncateId(item.afterNodeId) }}
            </span>

            <!-- Context -->
            <span v-if="item.context" class="text-slate-400 dark:text-slate-500">
              ({{ item.context }})
            </span>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  CheckCircleIcon,
  CheckIcon,
  ChevronRightIcon,
  ArrowUturnLeftIcon
} from '@heroicons/vue/24/outline'
import type { ChangelogItem } from '../types'

const props = defineProps<{
  changelog: ChangelogItem[]
  /** Whether this is the most recent changelog card in the thread */
  isLatest?: boolean
  /** Whether this changelog has already been undone */
  isUndone?: boolean
}>()

const emit = defineEmits<{
  undo: []
}>()

const isExpanded = ref(false)
const isUndoing = ref(false)

const addedCount = computed(() =>
  props.changelog.filter(c => c.type === 'add').length
)

const modifiedCount = computed(() =>
  props.changelog.filter(c => c.type === 'modify').length
)

const removedCount = computed(() =>
  props.changelog.filter(c => c.type === 'remove').length
)

function getResourceLabel(resourceType: ChangelogItem['resourceType']): string {
  const labels: Record<string, string> = {
    question: 'question',
    questionOption: 'option',
    offer: 'offer',
    dimension: 'segment group',
    segment: 'segment',
    phrase: 'phrase',
    flow: 'flow',
    'flow.node': 'node',
    flow_node: 'node',
    widget: 'widget',
    campaign: 'campaign',
    variant: 'variant',
    action: 'action',
    tag: 'tag',
    delay: 'delay',
    email_template: 'email template',
    email: 'email',
    page: 'page',
    yesno: 'condition',
  }
  return labels[resourceType] ?? resourceType
}

function truncateId(id: string): string {
  if (id.length <= 16) return id
  return `${id.slice(0, 8)}...${id.slice(-4)}`
}

async function handleUndo() {
  isUndoing.value = true
  emit('undo')
  // Safety reset — if undo hasn't completed after 15s, assume failure
  setTimeout(() => {
    if (!props.isUndone) {
      isUndoing.value = false
    }
  }, 15000)
}
</script>
